/* Banking Portal — Wave-6 Bank-Diligence pages (Feb 2026).

   Exports:
     - BankingRegulatory          (AAN · Complaints · Overrides · Allianz · Filings)
     - BankingPrivacy             (Consent · DSAR · PII reveals)
     - BankingDiligencePacket     (composite score + ZIP export)
     - BankingComplianceExtras    (NACHA card · used inline by Compliance page)
     - MoneyMapConcentrationTab   (used as 5th Money-Map tab)
     - TrustOperationsTab         (used as new Trust tab)
     - TrustActivityTab           (used as new Trust tab)
*/
import { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import {
  Scale, ScrollText, Lock, FileWarning, Shield, FileText, Download,
  AlertTriangle, CheckCircle2, Clock, Award, Activity, Building2, Users,
  Eye, Gauge, FileCheck, Server, Network,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "../../components/ui/tabs";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../../components/ui/select";
import {
  RefundQuotasPanel, LegalQueuePanel, UnderwritingMetricsPanel,
  TreasuryPositionCard, IntegrationsStatusCard,
} from "./BankingMirrorPages";

const API = process.env.REACT_APP_BACKEND_URL;

function fmtMoney(n) {
  if (n == null) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(n);
}
function fmtCompact(n) {
  if (n == null) return "—";
  return new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1, style: "currency", currency: "USD" }).format(n);
}
function fmtTime(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleString(); } catch { return s; }
}
function Kpi({ label, value, sub, tone = "slate", icon: Icon, testid }) {
  return (
    <div data-testid={testid} className={`rounded-xl border bg-${tone}-50 border-${tone}-200 px-4 py-3`}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
        {Icon && <Icon className={`w-4 h-4 text-${tone}-500`} />}
      </div>
      <div className="text-2xl font-semibold tabular-nums text-slate-900 mt-1.5">{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// NACHA metrics card (rendered inline in Compliance page as a new tab)
// ═══════════════════════════════════════════════════════════════════════
export function NachaMetricsPanel() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(60);

  useEffect(() => {
    api.get("/partner/banking/compliance/nacha-metrics", { params: { days } }).then(r => setData(r.data));
  }, [days]);
  if (!data) return <div className="text-sm text-slate-500" data-testid="nacha-loading">Loading…</div>;

  const StatusBadge = ({ s }) => {
    const tone = s === "breach" ? "rose" : s === "warning" ? "amber" : "emerald";
    const lbl = s === "breach" ? "BREACH" : s === "warning" ? "WARNING" : "OK";
    return <Badge variant="outline" className={`bg-${tone}-50 text-${tone}-700 border-${tone}-200 text-[10px]`}>{lbl}</Badge>;
  };

  return (
    <div data-testid="nacha-panel" className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-medium text-slate-700">NACHA performance · FFIEC limits</h2>
        <Select value={String(days)} onValueChange={v => setDays(Number(v))}>
          <SelectTrigger className="w-32 h-9" data-testid="nacha-days"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">7 days</SelectItem>
            <SelectItem value="30">30 days</SelectItem>
            <SelectItem value="60">60 days</SelectItem>
            <SelectItem value="90">90 days</SelectItem>
            <SelectItem value="180">180 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="nacha-kpis">
        <Kpi testid="nacha-total"  label="Total ACH txns" value={data.total_ach_txns.toLocaleString()} icon={Activity} tone="slate" />
        <Kpi testid="nacha-return" label="Return rate"    value={`${data.rates_pct.return}%`} sub={`limit ${data.ffiec_limits_pct.return}%`} tone={data.status.return === "breach" ? "rose" : data.status.return === "warning" ? "amber" : "emerald"} />
        <Kpi testid="nacha-unauth" label="Unauthorised"   value={`${data.rates_pct.unauthorised}%`} sub={`limit ${data.ffiec_limits_pct.unauthorised}%`} tone={data.status.unauthorised === "breach" ? "rose" : data.status.unauthorised === "warning" ? "amber" : "emerald"} />
        <Kpi testid="nacha-admin"  label="Administrative" value={`${data.rates_pct.administrative}%`} sub={`limit ${data.ffiec_limits_pct.administrative}%`} tone={data.status.administrative === "breach" ? "rose" : data.status.administrative === "warning" ? "amber" : "emerald"} />
      </div>
      <div className="rounded-2xl border bg-white p-4 text-xs text-slate-600">
        <div className="flex items-center gap-2 mb-2">
          <FileCheck className="w-4 h-4 text-slate-500" />
          <span className="font-medium text-sm text-slate-700">Status vs FFIEC limits</span>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div className="flex items-center justify-between bg-slate-50 rounded px-3 py-2"><span>Return</span><StatusBadge s={data.status.return} /></div>
          <div className="flex items-center justify-between bg-slate-50 rounded px-3 py-2"><span>Unauthorised</span><StatusBadge s={data.status.unauthorised} /></div>
          <div className="flex items-center justify-between bg-slate-50 rounded px-3 py-2"><span>Administrative</span><StatusBadge s={data.status.administrative} /></div>
        </div>
        <p className="mt-3 italic">FFIEC requires return &lt;15%, unauthorised &lt;0.5%, admin &lt;3%. NACHA WSUD-tier monitoring kicks in when any threshold is breached.</p>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Money Map · Concentration tab (5th tab on the Money Map page)
// ═══════════════════════════════════════════════════════════════════════
export function MoneyMapConcentrationTab() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/partner/banking/money-map/concentration", { params: { top: 10 } }).then(r => setData(r.data));
  }, []);
  if (!data) return <div className="text-sm text-slate-500" data-testid="conc-loading">Loading…</div>;

  const tone = data.hhi_category === "concentrated" ? "rose" : data.hhi_category === "moderately_concentrated" ? "amber" : "emerald";

  return (
    <div data-testid="mm-concentration-panel" className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="conc-kpis">
        <Kpi testid="conc-total"   label="Customer funds"   value={fmtCompact(data.customer_funds_total_usd)} tone="sky" />
        <Kpi testid="conc-hhi"     label="HHI"              value={data.hhi.toLocaleString()} sub={data.hhi_category.replace(/_/g, " ")} tone={tone} />
        <Kpi testid="conc-largest" label="Largest share"    value={`${data.largest_share_pct}%`} sub={`limit ${data.concentration_limit_pct}%`} tone={data.largest_share_pct >= data.concentration_limit_pct ? "rose" : "emerald"} icon={AlertTriangle} />
        <Kpi testid="conc-breach"  label="Single-name breaches" value={data.single_name_breach_count} tone={data.single_name_breach_count ? "rose" : "emerald"} />
      </div>

      <div className="rounded-2xl border bg-white overflow-hidden">
        <div className="px-4 py-2 bg-slate-50 text-sm font-medium text-slate-700 border-b">Top 10 customers by DDA balance</div>
        <div className="grid grid-cols-12 px-4 py-2 bg-slate-50/60 text-[11px] uppercase tracking-wider text-slate-500 border-b">
          <div className="col-span-1">#</div>
          <div className="col-span-4">Customer</div>
          <div className="col-span-2">Role</div>
          <div className="col-span-2">KYB</div>
          <div className="col-span-2 text-right">Balance</div>
          <div className="col-span-1 text-right">Share</div>
        </div>
        <div data-testid="conc-table">
          {data.top_rows.map((r, i) => {
            const over = r.share_pct >= data.concentration_limit_pct;
            return (
              <div key={r.wallet_user_id} data-testid={`conc-row-${r.wallet_user_id}`} className={`grid grid-cols-12 px-4 py-2.5 border-b border-slate-100 text-sm items-center ${over ? "bg-rose-50/40" : ""}`}>
                <div className="col-span-1 text-xs text-slate-500">{i + 1}</div>
                <div className="col-span-4 truncate">
                  <div className="truncate">{r.owner_company || "—"}</div>
                  <div className="text-[10px] text-slate-500 truncate">{r.owner_email || "—"}</div>
                </div>
                <div className="col-span-2"><Badge variant="outline" className="text-[10px] uppercase">{r.owner_role || "—"}</Badge></div>
                <div className="col-span-2">{r.kyb_status ? <Badge variant="outline" className="text-[10px]">{r.kyb_status}</Badge> : <span className="text-xs text-slate-400">—</span>}</div>
                <div className="col-span-2 text-right tabular-nums">{fmtMoney(r.balance)}</div>
                <div className={`col-span-1 text-right text-xs ${over ? "text-rose-700 font-semibold" : "text-slate-700"}`}>{r.share_pct}%</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════
// REGULATORY — AAN · Complaints · Overrides · Allianz · Filings
// ═══════════════════════════════════════════════════════════════════════
export function BankingRegulatory() {
  const [tab, setTab] = useState("aan");

  return (
    <div data-testid="banking-regulatory" className="space-y-5">
      <header>
        <h1 className="display text-3xl tracking-tight flex items-center gap-2.5">
          <Shield className="w-6 h-6 text-sky-500" /> Regulatory & Reporting
        </h1>
        <p className="text-sm text-slate-600 mt-1">Adverse Action Notices, complaints log, admin override audit, Allianz cover, and SAR/CTR filings register.</p>
      </header>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="reg-tabs">
          <TabsTrigger value="aan"        data-testid="reg-tab-aan">Adverse Actions</TabsTrigger>
          <TabsTrigger value="complaints" data-testid="reg-tab-complaints">Complaints</TabsTrigger>
          <TabsTrigger value="overrides"  data-testid="reg-tab-overrides">Override audit</TabsTrigger>
          <TabsTrigger value="allianz"    data-testid="reg-tab-allianz">Allianz cover</TabsTrigger>
          <TabsTrigger value="filings"    data-testid="reg-tab-filings">SAR/CTR filings</TabsTrigger>
          <TabsTrigger value="quotas"     data-testid="reg-tab-quotas">Refund quotas</TabsTrigger>
          <TabsTrigger value="legal"      data-testid="reg-tab-legal">Legal queue</TabsTrigger>
          <TabsTrigger value="uw"         data-testid="reg-tab-uw">Underwriting</TabsTrigger>
        </TabsList>

        <TabsContent value="aan"        className="mt-4 space-y-3"><AdverseActionsPanel /></TabsContent>
        <TabsContent value="complaints" className="mt-4 space-y-3"><ComplaintsPanel /></TabsContent>
        <TabsContent value="overrides"  className="mt-4 space-y-3"><OverridesPanel /></TabsContent>
        <TabsContent value="allianz"    className="mt-4 space-y-3"><AllianzPanel /></TabsContent>
        <TabsContent value="filings"    className="mt-4 space-y-3"><FilingsPanel /></TabsContent>
        <TabsContent value="quotas"     className="mt-4 space-y-3"><RefundQuotasPanel /></TabsContent>
        <TabsContent value="legal"      className="mt-4 space-y-3"><LegalQueuePanel /></TabsContent>
        <TabsContent value="uw"         className="mt-4 space-y-3"><UnderwritingMetricsPanel /></TabsContent>
      </Tabs>
    </div>
  );
}

function AdverseActionsPanel() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(90);
  useEffect(() => {
    api.get("/partner/banking/compliance/adverse-actions", { params: { days } }).then(r => setData(r.data));
  }, [days]);
  if (!data) return <div className="text-sm text-slate-500" data-testid="aan-loading">Loading…</div>;

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="aan-kpis">
        <Kpi testid="aan-total"     label={`AANs · ${days}d`}        value={data.total.toLocaleString()} icon={FileWarning} tone="slate" />
        <Kpi testid="aan-on-time"   label="Delivered ≤30d"           value={`${data.delivery_within_30d_pct}%`} sub="Reg B requirement" tone={data.delivery_within_30d_pct >= 95 ? "emerald" : data.delivery_within_30d_pct >= 80 ? "amber" : "rose"} />
        <Kpi testid="aan-undeliv"   label="Not delivered"            value={(data.not_delivered || 0).toLocaleString()} tone={data.not_delivered ? "rose" : "emerald"} />
        <Kpi testid="aan-kinds"     label="Distinct AAN kinds"       value={Object.keys(data.by_kind).length.toLocaleString()} tone="slate" />
      </div>
      <div className="flex items-center gap-2 rounded-2xl border bg-white px-4 py-3">
        <Select value={String(days)} onValueChange={v => setDays(Number(v))}>
          <SelectTrigger className="w-32 h-9" data-testid="aan-days"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="30">30 days</SelectItem>
            <SelectItem value="90">90 days</SelectItem>
            <SelectItem value="180">180 days</SelectItem>
            <SelectItem value="365">365 days</SelectItem>
          </SelectContent>
        </Select>
        <div className="ml-auto text-xs text-slate-500" data-testid="aan-count">{data.rows.length} rows</div>
      </div>
      <div className="rounded-2xl border bg-white overflow-hidden">
        <div className="grid grid-cols-12 px-4 py-2 bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 border-b">
          <div className="col-span-2">When</div>
          <div className="col-span-3">Customer</div>
          <div className="col-span-2">Kind</div>
          <div className="col-span-2">Decision</div>
          <div className="col-span-3">Reasons</div>
        </div>
        <div data-testid="aan-table">
          {data.rows.length === 0 && <div className="text-center text-sm text-slate-500 py-10" data-testid="aan-empty">No adverse-action notices in window.</div>}
          {data.rows.slice(0, 50).map(r => (
            <div key={r.id} data-testid={`aan-row-${r.id}`} className="grid grid-cols-12 px-4 py-2 border-b border-slate-100 text-sm items-center">
              <div className="col-span-2 text-xs text-slate-500">{fmtTime(r.created_at)}</div>
              <div className="col-span-3 truncate text-xs">{r.buyer_company || r.customer_handle || "—"}</div>
              <div className="col-span-2 text-xs">{r.kind || "—"}</div>
              <div className="col-span-2"><Badge variant="outline" className={`text-[10px] ${r.decision === "approve" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : r.decision === "deny" ? "bg-rose-50 text-rose-700 border-rose-200" : ""}`}>{r.decision || "—"}</Badge></div>
              <div className="col-span-3 text-[11px] text-slate-600 truncate">{Array.isArray(r.reasons) ? r.reasons.join(", ") : (r.reasons || "—")}</div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

function ComplaintsPanel() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(90);
  useEffect(() => {
    api.get("/partner/banking/compliance/complaints", { params: { days } }).then(r => setData(r.data));
  }, [days]);
  if (!data) return <div className="text-sm text-slate-500" data-testid="complaints-loading">Loading…</div>;
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="complaints-kpis">
        <Kpi testid="complaints-total"     label={`Complaints · ${days}d`} value={data.total.toLocaleString()} icon={FileWarning} tone={data.total ? "amber" : "emerald"} />
        <Kpi testid="complaints-open"      label="Open" value={(data.by_status?.open || 0).toLocaleString()} tone={(data.by_status?.open || 0) ? "amber" : "slate"} />
        <Kpi testid="complaints-critical"  label="Critical" value={(data.by_severity?.critical || 0).toLocaleString()} tone={(data.by_severity?.critical || 0) ? "rose" : "slate"} />
        <Kpi testid="complaints-avg"       label="Avg resolution" value={data.avg_resolution_hours ? `${data.avg_resolution_hours}h` : "—"} tone="slate" />
      </div>
      <div className="flex items-center gap-2 rounded-2xl border bg-white px-4 py-3">
        <Select value={String(days)} onValueChange={v => setDays(Number(v))}>
          <SelectTrigger className="w-32 h-9" data-testid="complaints-days"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="30">30 days</SelectItem>
            <SelectItem value="90">90 days</SelectItem>
            <SelectItem value="180">180 days</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="rounded-2xl border bg-white overflow-hidden" data-testid="complaints-table">
        {data.total === 0 ? (
          <div className="text-center text-sm text-slate-500 py-10" data-testid="complaints-empty">
            <CheckCircle2 className="w-4 h-4 inline mr-1.5 text-emerald-500" /> No complaints in window. (Admins can file complaints via <span className="font-mono">/api/admin/complaints</span>.)
          </div>
        ) : data.rows.slice(0, 50).map(r => (
          <div key={r.id} data-testid={`complaints-row-${r.id}`} className="px-4 py-2.5 border-b border-slate-100 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">{fmtTime(r.created_at)}</span>
              <Badge variant="outline" className="text-[10px]">{r.category}</Badge>
              <Badge variant="outline" className="text-[10px]">{r.severity}</Badge>
              <Badge variant="outline" className="text-[10px]">{r.status}</Badge>
            </div>
            <div className="text-xs text-slate-700 mt-1 truncate">{r.summary}</div>
          </div>
        ))}
      </div>
    </>
  );
}

function OverridesPanel() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  useEffect(() => {
    api.get("/partner/banking/compliance/admin-overrides", { params: { days } }).then(r => setData(r.data));
  }, [days]);
  if (!data) return <div className="text-sm text-slate-500" data-testid="overrides-loading">Loading…</div>;
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="overrides-kpis">
        <Kpi testid="overrides-total"   label={`Overrides · ${days}d`} value={data.total.toLocaleString()} icon={Shield} tone="slate" />
        <Kpi testid="overrides-impact"  label="Total $ impact" value={fmtCompact(data.total_impact_usd)} tone="slate" />
        <Kpi testid="overrides-actions" label="Distinct actions" value={Object.keys(data.by_action).length.toLocaleString()} tone="slate" />
        <Kpi testid="overrides-chained" label="Hash chained" value="✓ All" tone="emerald" />
      </div>
      <div className="flex items-center gap-2 rounded-2xl border bg-white px-4 py-3">
        <Select value={String(days)} onValueChange={v => setDays(Number(v))}>
          <SelectTrigger className="w-32 h-9" data-testid="overrides-days"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">7 days</SelectItem>
            <SelectItem value="30">30 days</SelectItem>
            <SelectItem value="90">90 days</SelectItem>
            <SelectItem value="180">180 days</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="rounded-2xl border bg-white overflow-hidden">
        <div className="grid grid-cols-12 px-4 py-2 bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 border-b">
          <div className="col-span-2">When</div>
          <div className="col-span-3">Action</div>
          <div className="col-span-2">Actor</div>
          <div className="col-span-2">Target type</div>
          <div className="col-span-2 text-right">Impact</div>
          <div className="col-span-1 text-right">Pos</div>
        </div>
        <div data-testid="overrides-table">
          {data.rows.slice(0, 50).map((r, i) => (
            <div key={r.id || `ovr-${i}`} data-testid={`overrides-row-${r.id}`} className="grid grid-cols-12 px-4 py-2 border-b border-slate-100 text-sm items-center">
              <div className="col-span-2 text-xs text-slate-500">{fmtTime(r.created_at)}</div>
              <div className="col-span-3 font-mono text-xs">{r.action || "—"}</div>
              <div className="col-span-2 text-xs text-slate-600">{r.actor_handle}</div>
              <div className="col-span-2 text-xs text-slate-500">{r.target_type || "—"}</div>
              <div className="col-span-2 tabular-nums text-right text-xs">{r.impact_usd ? fmtMoney(r.impact_usd) : "—"}</div>
              <div className="col-span-1 text-right font-mono text-[10px] text-slate-400">{r.chain_position ?? "—"}</div>
            </div>
          ))}
          {data.rows.length === 0 && <div className="text-center text-sm text-slate-500 py-10" data-testid="overrides-empty">No overrides in window.</div>}
        </div>
      </div>
    </>
  );
}

function AllianzPanel() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/partner/banking/compliance/allianz-coverage").then(r => setData(r.data));
  }, []);
  if (!data) return <div className="text-sm text-slate-500" data-testid="allianz-loading">Loading…</div>;
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="allianz-kpis">
        <Kpi testid="allianz-buyers"   label="Total buyers"     value={(data.total_buyers || 0).toLocaleString()} icon={Users} tone="slate" />
        <Kpi testid="allianz-covered"  label="Covered"          value={(data.covered_buyers || 0).toLocaleString()} sub={`${data.covered_pct}% of buyers`} tone={data.covered_pct >= 80 ? "emerald" : data.covered_pct >= 50 ? "amber" : "rose"} />
        <Kpi testid="allianz-limit"    label="Total coverage"   value={fmtCompact(data.total_coverage_usd)} tone="emerald" icon={Award} />
        <Kpi testid="allianz-pastdue"  label="Past-due open"    value={(data.past_due_open_loans || 0).toLocaleString()} tone={data.past_due_open_loans ? "amber" : "emerald"} />
      </div>
      <div className="rounded-2xl border bg-white p-4 text-xs text-slate-600 italic" data-testid="allianz-attest">
        {data.attestation_text}
      </div>
    </>
  );
}

function FilingsPanel() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/partner/banking/compliance/regulatory-filings").then(r => setData(r.data));
  }, []);
  if (!data) return <div className="text-sm text-slate-500" data-testid="filings-loading">Loading…</div>;
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="filings-kpis">
        <Kpi testid="filings-total" label="Filings · 180d" value={data.total.toLocaleString()} icon={FileCheck} tone="slate" />
        <Kpi testid="filings-sar"   label="SARs"  value={(data.by_kind?.sar || 0).toLocaleString()} tone="slate" />
        <Kpi testid="filings-ctr"   label="CTRs"  value={(data.by_kind?.ctr || 0).toLocaleString()} tone="slate" />
        <Kpi testid="filings-314a"  label="314(a)" value={(data.by_kind?.["314a"] || 0).toLocaleString()} tone="slate" />
      </div>
      <div className="rounded-2xl border bg-white overflow-hidden" data-testid="filings-table">
        {data.total === 0 ? (
          <div className="text-center text-sm text-slate-500 py-10" data-testid="filings-empty">
            No regulatory filings yet. Admins can file via <span className="font-mono">/api/admin/regulatory-filings</span>.
          </div>
        ) : data.rows.slice(0, 50).map(r => (
          <div key={r.id} data-testid={`filings-row-${r.id}`} className="px-4 py-2.5 border-b border-slate-100 text-sm">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-[10px] uppercase">{r.kind}</Badge>
              <Badge variant="outline" className="text-[10px]">{r.status}</Badge>
              <span className="font-mono text-xs">{r.reference_number}</span>
              {r.amount_usd ? <span className="tabular-nums text-xs">{fmtMoney(r.amount_usd)}</span> : null}
              <span className="text-xs text-slate-500 ml-auto">{fmtTime(r.filed_at)}</span>
            </div>
            <div className="text-xs text-slate-700 mt-1 truncate">{r.narrative}</div>
          </div>
        ))}
      </div>
    </>
  );
}


// ═══════════════════════════════════════════════════════════════════════
// PRIVACY — Consent · DSAR · PII reveals
// ═══════════════════════════════════════════════════════════════════════
export function BankingPrivacy() {
  const [tab, setTab] = useState("consent");
  return (
    <div data-testid="banking-privacy" className="space-y-5">
      <header>
        <h1 className="display text-3xl tracking-tight flex items-center gap-2.5">
          <Eye className="w-6 h-6 text-sky-500" /> Privacy & Consent
        </h1>
        <p className="text-sm text-slate-600 mt-1">User-consent audit trail, GLBA/CCPA data-subject-request register, and PII reveal log.</p>
      </header>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="privacy-tabs">
          <TabsTrigger value="consent" data-testid="privacy-tab-consent">Consent</TabsTrigger>
          <TabsTrigger value="dsar"    data-testid="privacy-tab-dsar">DSAR</TabsTrigger>
          <TabsTrigger value="reveals" data-testid="privacy-tab-reveals">PII reveals</TabsTrigger>
        </TabsList>
        <TabsContent value="consent" className="mt-4 space-y-3"><ConsentPanel /></TabsContent>
        <TabsContent value="dsar"    className="mt-4 space-y-3"><DsarPanel /></TabsContent>
        <TabsContent value="reveals" className="mt-4 space-y-3"><PiiPanel /></TabsContent>
      </Tabs>
    </div>
  );
}

function ConsentPanel() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/partner/banking/privacy/consent-audit").then(r => setData(r.data));
  }, []);
  if (!data) return <div className="text-sm text-slate-500" data-testid="consent-loading">Loading…</div>;
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="consent-kpis">
        <Kpi testid="consent-total"     label={`Consents · ${data.window_days}d`} value={data.total_consents.toLocaleString()} icon={FileCheck} tone="slate" />
        <Kpi testid="consent-users"     label="Active users"  value={data.active_users.toLocaleString()} icon={Users} tone="slate" />
        <Kpi testid="consent-docs"      label="Distinct docs" value={Object.keys(data.by_doc_id).length.toLocaleString()} tone="slate" />
      </div>
      <div className="rounded-2xl border bg-white p-4">
        <div className="text-sm font-medium text-slate-700 mb-2">By document</div>
        <div className="grid grid-cols-2 gap-2 text-xs" data-testid="consent-by-doc">
          {Object.entries(data.by_doc_id).map(([d, n]) => (
            <div key={d} className="flex justify-between bg-slate-50 rounded px-3 py-2"><span className="font-mono">{d}</span><span className="tabular-nums">{n}</span></div>
          ))}
        </div>
        <div className="text-xs text-slate-500 mt-3 italic">{data.append_only_attestation}</div>
      </div>
    </>
  );
}

function DsarPanel() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/partner/banking/privacy/data-subject-requests").then(r => setData(r.data));
  }, []);
  if (!data) return <div className="text-sm text-slate-500" data-testid="dsar-loading">Loading…</div>;
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="dsar-kpis">
        <Kpi testid="dsar-total"       label={`DSARs · ${data.window_days}d`}  value={data.total.toLocaleString()} icon={Users} tone="slate" />
        <Kpi testid="dsar-avg"         label="Avg turnaround" value={data.avg_turnaround_days ? `${data.avg_turnaround_days}d` : "—"} tone="slate" />
        <Kpi testid="dsar-compliance"  label="On-time"        value={`${data.compliance_pct}%`} sub={`target ≤${data.compliance_target_days}d`} tone={data.compliance_pct >= 95 ? "emerald" : data.compliance_pct >= 80 ? "amber" : "rose"} />
        <Kpi testid="dsar-target"      label="Target"         value={`${data.compliance_target_days}d`} sub="GLBA/CCPA" tone="slate" />
      </div>
      <div className="rounded-2xl border bg-white overflow-hidden" data-testid="dsar-table">
        {data.total === 0 ? (
          <div className="text-center text-sm text-slate-500 py-10" data-testid="dsar-empty"><CheckCircle2 className="w-4 h-4 inline mr-1.5 text-emerald-500" /> No DSARs in window.</div>
        ) : data.rows.slice(0, 50).map(r => (
          <div key={r.id} data-testid={`dsar-row-${r.id}`} className="px-4 py-2 border-b border-slate-100 text-sm flex items-center justify-between">
            <div>
              <div className="text-xs">{r.kind || "deletion"} · {r.method || "—"}</div>
              <div className="text-[10px] text-slate-500">requested {fmtTime(r.requested_at)}</div>
            </div>
            <Badge variant="outline" className={`text-[10px] ${r.completed_at ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>{r.completed_at ? "complete" : "open"}</Badge>
          </div>
        ))}
      </div>
    </>
  );
}

function PiiPanel() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/partner/banking/privacy/pii-reveals").then(r => setData(r.data));
  }, []);
  if (!data) return <div className="text-sm text-slate-500" data-testid="pii-loading">Loading…</div>;
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="pii-kpis">
        <Kpi testid="pii-total" label={`PII reveals · ${data.window_days}d`} value={data.total.toLocaleString()} icon={Eye} tone="slate" />
        <Kpi testid="pii-wrong" label="Wrong password" value={data.wrong_pw.toLocaleString()} tone={data.wrong_pw ? "rose" : "emerald"} />
        <Kpi testid="pii-fields" label="Distinct fields" value={Object.keys(data.by_field).length.toLocaleString()} tone="slate" />
      </div>
      <div className="rounded-2xl border bg-white p-4 text-xs">
        <div className="font-medium text-sm text-slate-700 mb-2">By outcome / field</div>
        <div className="grid grid-cols-2 gap-2" data-testid="pii-by-field">
          {Object.entries(data.by_field).map(([k, n]) => (
            <div key={k} className="flex justify-between bg-slate-50 rounded px-3 py-2"><span className="font-mono">{k}</span><span className="tabular-nums">{n}</span></div>
          ))}
        </div>
      </div>
    </>
  );
}


// ═══════════════════════════════════════════════════════════════════════
// TRUST — additional tabs: Operations · Activity · Vendors
// ═══════════════════════════════════════════════════════════════════════
export function TrustOperationsTab() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/partner/banking/trust/operational-resilience").then(r => setData(r.data));
  }, []);
  if (!data) return <div className="text-sm text-slate-500" data-testid="ops-loading">Loading…</div>;
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="ops-kpis">
        <Kpi testid="ops-sla-24h" label="SLA · 24h" value={`${data.sla_pct["24h"]}%`} sub={`${data.windows["24h"].calls.toLocaleString()} calls · ${data.windows["24h"].errors} errors`} tone={data.sla_pct["24h"] >= 99.9 ? "emerald" : data.sla_pct["24h"] >= 99.0 ? "amber" : "rose"} icon={Gauge} />
        <Kpi testid="ops-sla-30d" label="SLA · 30d" value={`${data.sla_pct["30d"]}%`} sub={`${data.windows["30d"].calls.toLocaleString()} calls`} tone={data.sla_pct["30d"] >= 99.9 ? "emerald" : data.sla_pct["30d"] >= 99.0 ? "amber" : "rose"} icon={Gauge} />
        <Kpi testid="ops-sla-90d" label="SLA · 90d" value={`${data.sla_pct["90d"]}%`} sub={`${data.windows["90d"].calls.toLocaleString()} calls`} tone={data.sla_pct["90d"] >= 99.9 ? "emerald" : data.sla_pct["90d"] >= 99.0 ? "amber" : "rose"} icon={Gauge} />
        <Kpi testid="ops-incidents-30d" label="5xx incidents · 30d" value={data.incident_proxy_30d.toLocaleString()} tone={data.incident_proxy_30d ? "amber" : "emerald"} icon={AlertTriangle} />
        <Kpi testid="ops-rto" label="RTO target" value={`${data.rto_target_min}m`} tone="slate" icon={Clock} />
        <Kpi testid="ops-rpo" label="RPO target" value={`${data.rpo_target_min}m`} tone="slate" icon={Clock} />
      </div>
      <div className="rounded-2xl border bg-white p-4 text-xs text-slate-600">
        <div className="font-medium text-sm text-slate-700 mb-2">Backup & DR policy</div>
        <div>{data.backup_policy}</div>
        <div className="mt-2">P1 incident response target: <span className="font-mono">{data.incident_target_p1_response_min} min</span> · P2: <span className="font-mono">{data.incident_target_p2_response_min} min</span></div>
      </div>
      <TreasuryPositionCard />
    </>
  );
}

export function TrustActivityTab() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(7);
  useEffect(() => {
    api.get("/partner/banking/trust/activity-log", { params: { days } }).then(r => setData(r.data));
  }, [days]);
  if (!data) return <div className="text-sm text-slate-500" data-testid="activity-loading">Loading…</div>;
  return (
    <>
      <div className="flex items-center gap-2 rounded-2xl border bg-white px-4 py-3">
        <Select value={String(days)} onValueChange={v => setDays(Number(v))}>
          <SelectTrigger className="w-32 h-9" data-testid="activity-days"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="1">24h</SelectItem>
            <SelectItem value="7">7 days</SelectItem>
            <SelectItem value="30">30 days</SelectItem>
            <SelectItem value="60">60 days</SelectItem>
          </SelectContent>
        </Select>
        <div className="ml-auto text-xs text-slate-500" data-testid="activity-count">{data.rows.length} entries</div>
      </div>
      <div className="rounded-2xl border bg-white overflow-hidden" data-testid="activity-table">
        {data.rows.length === 0 && <div className="text-center text-sm text-slate-500 py-10" data-testid="activity-empty">No admin activity in window.</div>}
        {data.rows.slice(0, 50).map((r, i) => (
          <div key={i} className="grid grid-cols-12 px-4 py-2 border-b border-slate-100 text-sm">
            <div className="col-span-3 text-xs text-slate-500">{fmtTime(r.ts)}</div>
            <div className="col-span-3 font-mono text-xs">{r.type || "—"}</div>
            <div className="col-span-3 font-mono text-xs">{r.action || "—"}</div>
            <div className="col-span-3 font-mono text-xs text-slate-500">{r.actor_id_short}</div>
          </div>
        ))}
      </div>
    </>
  );
}

export function TrustVendorsTab() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/partner/banking/trust/vendor-monitoring").then(r => setData(r.data));
  }, []);
  if (!data) return <div className="text-sm text-slate-500" data-testid="vendors-loading">Loading…</div>;
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="vendors-kpis">
        <Kpi testid="vendors-total"    label="Sub-processors" value={data.total.toLocaleString()} icon={Server} tone="slate" />
        <Kpi testid="vendors-expiring" label="Renewal ≤90d"   value={data.expiring_soon.toLocaleString()} tone={data.expiring_soon ? "amber" : "emerald"} />
        <Kpi testid="vendors-expired"  label="Expired"        value={data.expired.toLocaleString()} tone={data.expired ? "rose" : "emerald"} />
      </div>
      <div className="rounded-2xl border bg-white overflow-hidden" data-testid="vendors-table">
        <div className="grid grid-cols-12 px-4 py-2 bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 border-b">
          <div className="col-span-3">Vendor</div>
          <div className="col-span-4">Purpose</div>
          <div className="col-span-2">Tier</div>
          <div className="col-span-3">SOC status</div>
        </div>
        {data.vendors.map(v => {
          const tone = v.soc_status === "expired" ? "rose" : v.soc_status === "renewal_due_30d" ? "rose" : v.soc_status === "renewal_due_90d" ? "amber" : "emerald";
          return (
            <div key={v.name} data-testid={`vendor-row-${v.name.toLowerCase().replace(/\s+/g, "-")}`} className="grid grid-cols-12 px-4 py-2 border-b border-slate-100 text-sm items-center">
              <div className="col-span-3 font-medium text-slate-900">{v.name}</div>
              <div className="col-span-4 text-xs text-slate-600">{v.purpose}</div>
              <div className="col-span-2"><Badge variant="outline" className="text-[10px]">{v.tier}</Badge></div>
              <div className="col-span-3"><Badge variant="outline" className={`bg-${tone}-50 text-${tone}-700 border-${tone}-200 text-[10px]`}>{v.soc_status || "—"}{v.next_review_due ? ` · due ${v.next_review_due}` : ""}</Badge></div>
            </div>
          );
        })}
      </div>
      <IntegrationsStatusCard />
    </>
  );
}


// ═══════════════════════════════════════════════════════════════════════
// DILIGENCE PACKET — hero score + one-click ZIP export
// ═══════════════════════════════════════════════════════════════════════
export function BankingDiligencePacket() {
  const [data, setData] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const load = () => api.get("/partner/banking/diligence/score").then(r => setData(r.data));
  useEffect(() => { load(); }, []);

  const downloadZip = async () => {
    setDownloading(true);
    try {
      const url = `${API}/api/partner/banking/diligence/packet.zip`;
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error("Download failed");
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `verqo_diligence_${Date.now()}.zip`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Diligence packet downloaded");
    } catch (e) {
      toast.error("Download failed");
    } finally { setDownloading(false); }
  };

  if (!data) return <div className="text-sm text-slate-500" data-testid="diligence-loading">Loading…</div>;

  const gradeColor = data.grade === "A" ? "emerald" : data.grade === "B" ? "emerald" : data.grade === "C" ? "amber" : data.grade === "D" ? "amber" : "rose";

  return (
    <div data-testid="banking-diligence" className="space-y-5">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="display text-3xl tracking-tight flex items-center gap-2.5">
            <Award className="w-6 h-6 text-sky-500" /> Diligence Packet
          </h1>
          <p className="text-sm text-slate-600 mt-1">Live 0-100 bank-readiness score with full-packet ZIP export (every dashboard + every compliance PDF).</p>
        </div>
        <Button onClick={downloadZip} disabled={downloading} data-testid="diligence-download">
          <Download className="w-4 h-4 mr-1.5" /> {downloading ? "Building…" : "Download ZIP"}
        </Button>
      </header>

      <div className="rounded-2xl border bg-white p-6 flex items-center gap-6" data-testid="diligence-hero">
        <div className={`w-32 h-32 rounded-full bg-${gradeColor}-50 border-4 border-${gradeColor}-200 grid place-items-center`}>
          <div className="text-center">
            <div className={`text-5xl font-bold text-${gradeColor}-700`} data-testid="diligence-grade">{data.grade}</div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-0.5">Grade</div>
          </div>
        </div>
        <div className="flex-1">
          <div className="flex items-end gap-2">
            <div className={`text-5xl font-semibold text-${gradeColor}-700 tabular-nums`} data-testid="diligence-score">{data.composite_score}</div>
            <div className="text-sm text-slate-500 pb-2">/ 100</div>
          </div>
          <div className="text-sm text-slate-600 mt-1">Composite of DDA segregation, sanctions, AML, KYB, audit chain, concentration, NACHA, consent, AAN delivery, SOC docs.</div>
          <div className="mt-3 h-3 rounded-full bg-slate-100 overflow-hidden">
            <div className={`h-full bg-${gradeColor}-500 transition-all`} style={{ width: `${Math.min(data.composite_score, 100)}%` }} />
          </div>
        </div>
      </div>

      <div className="rounded-2xl border bg-white overflow-hidden" data-testid="diligence-lines">
        <div className="px-4 py-2 bg-slate-50 text-sm font-medium text-slate-700 border-b">Score breakdown</div>
        <div className="grid grid-cols-12 px-4 py-2 bg-slate-50/60 text-[11px] uppercase tracking-wider text-slate-500 border-b">
          <div className="col-span-4">Dimension</div>
          <div className="col-span-2 text-right">Points</div>
          <div className="col-span-1 text-right">Weight</div>
          <div className="col-span-2 text-right">Contribution</div>
          <div className="col-span-3">Note</div>
        </div>
        {data.lines.map(L => {
          const tone = L.points >= 90 ? "emerald" : L.points >= 70 ? "amber" : "rose";
          return (
            <div key={L.name} data-testid={`diligence-line-${L.name.toLowerCase().replace(/\s+/g, "-")}`} className="grid grid-cols-12 px-4 py-2.5 border-b border-slate-100 text-sm items-center">
              <div className="col-span-4 font-medium">{L.name}</div>
              <div className={`col-span-2 text-right tabular-nums font-mono text-${tone}-700`}>{L.points.toFixed(1)}</div>
              <div className="col-span-1 text-right text-xs text-slate-500">{L.weight}%</div>
              <div className="col-span-2 text-right tabular-nums font-mono">{L.weighted.toFixed(2)}</div>
              <div className="col-span-3 text-xs text-slate-500 truncate">{L.note}</div>
            </div>
          );
        })}
      </div>
      <div className="text-[11px] text-slate-500 text-right" data-testid="diligence-asof">As of {fmtTime(data.as_of)}</div>
    </div>
  );
}
